/**
 * Crew pay (features.crewPay): #/pay (a month's crew invoices: make, send, pay, exports), #/pay/<id> (one crew
 * invoice) and #/pay/details (pay rates and each crew member's pay details). The crew member's own statements are
 * MyPay, in the app for site staff.
 */
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { PageHeader, plural } from "../../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton, SkeletonLines, type ChipTone } from "../../components/States";
import { BRAND } from "../../lib/config";
import { aud, fmtDate, fmtPlainDate, localYmd } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { hashParams, useHash } from "../../lib/route";
import { useAsync } from "../../lib/useAsync";
import {
  banks,
  confirmStatement,
  crewInvoice,
  crewInvoicePdf,
  disputeAmount,
  listCrewInvoices,
  makeMonth,
  markPaid,
  myRole,
  myStatementPdf,
  myStatements,
  payMembers,
  paymentsFile,
  payRates,
  previewMonth,
  queryStatement,
  removeOwnRates,
  revalidate,
  saveBank,
  savePayDetails,
  saveBlob,
  saveRates,
  sendCrewInvoice,
  STATUS_LABEL,
  superReport,
  voidCrewInvoice,
  type CrewInvoiceRow,
  type PayMember,
  type Rates,
  type Status,
} from "./pay-data";
import { PAY_NAV } from "./nav";

export const crewPayInUse = () => Boolean(BRAND.crewPay);

const TONE: Record<Status, ChipTone> = { issued: "outline", confirmed: "accent", queried: "primary", paid: "green", void: "primary-outline" };
const chip = (i: Pick<CrewInvoiceRow, "status" | "is_valid">) => (i.status !== "void" && i.status !== "paid" && !i.is_valid ? <Chip tone="primary">Not valid yet</Chip> : <Chip tone={TONE[i.status]}>{STATUS_LABEL[i.status]}</Chip>);
const monthLabel = (ym: string) => new Intl.DateTimeFormat("en-AU", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${ym.slice(0, 7)}-01T12:00:00Z`));
const lastMonth = () => {
  const d = new Date(`${localYmd().slice(0, 7)}-01T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
};
const hrs = (m: number) => (m / 60).toFixed(2);
const payable = (i: Pick<CrewInvoiceRow, "total" | "disputed_amount">) => Math.round((i.total - (i.disputed_amount ?? 0)) * 100) / 100;

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return (
    <label className={`field${wide ? " field--wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function CrewPay() {
  const [id] = hashParams(useHash());
  if (id === "details") return <PayDetails />;
  if (id) return <CrewInvoiceView key={id} id={id} />;
  return <Month />;
}

function Tabs({ current }: { current: "month" | "details" }) {
  return (
    <nav className="form-actions" aria-label="Crew pay sections" style={{ marginBottom: 16 }}>
      <a className={`btn btn--sm ${current === "month" ? "btn--ink" : "btn--outline"}`} href="#/pay" aria-current={current === "month" ? "page" : undefined}>Crew invoices</a>
      <a className={`btn btn--sm ${current === "details" ? "btn--ink" : "btn--outline"}`} href="#/pay/details" aria-current={current === "details" ? "page" : undefined}>Pay rates and details</a>
    </nav>
  );
}

/* ------------------------------------------------------------------ a month */

function Month() {
  const [month, setMonth] = useState(lastMonth());
  const list = useAsync(() => listCrewInvoices(month), [month]);
  const role = useAsync(myRole);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof previewMonth>> | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const rows = (list.data ?? []).filter((i) => i.status !== "void");
  const director = role.data === "director";

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader eyebrow="Accounts" title={PAY_NAV.label} meta="Each month, approved docket hours become a crew invoice for each subcontractor, to check, send and pay." />
      <Tabs current="month" />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="card">
        <div className="card__body form-grid">
          <Field label="Month"><input className="input" type="month" value={month} max={localYmd().slice(0, 7)} onChange={(e) => { setPreview(null); setMonth(e.target.value); }} /></Field>
          <div className="form-actions field--wide">
            <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => void run(async () => setPreview(await previewMonth(month)))}>Check {monthLabel(month)}</button>
            {director && <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => void run(async () => saveBlob(await paymentsFile(), `crew-payments-${localYmd()}.csv`))}><Icon name="file" size={16} /> Payments file</button>}
            <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => void run(async () => saveBlob(await superReport(month), `super-liable-pay-${month}.csv`))}><Icon name="file" size={16} /> Super report</button>
          </div>
          <p className="small muted field--wide">Crew invoices for last month are made on the 1st. Check a month to see the hours waiting, then make them now if you need to. Later hours go on a second crew invoice.</p>
        </div>
      </div>

      {preview && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">Hours waiting for {monthLabel(preview.month)}</h2></div>
          <div className="card__body stack">
            {!preview.people.length && <p className="small muted">No approved docket hours waiting for this month.</p>}
            {preview.people.length > 0 && (
              <ul className="list">
                {preview.people.map((p) => (
                  <li key={p.crewMemberId} className="list__row">
                    <span>
                      <strong>{p.name}</strong>
                      <span className="sub">{p.partner ? "Partner: not paid by crew invoice" : `${plural(p.lines, "shift")}, ${hrs(p.minutes)} hours`}</span>
                      {p.problems.map((x) => <span key={x} className="sub sub--primary">{x}</span>)}
                    </span>
                    {!p.partner && <span className="list__value">{aud(p.amount)}</span>}
                  </li>
                ))}
              </ul>
            )}
            {preview.people.some((p) => !p.partner && p.lines) && (
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={busy}
                  aria-busy={busy}
                  onClick={() =>
                    void run(async () => {
                      const r = await makeMonth(month);
                      ok(`${plural(r.made.length, "crew invoice")} made.${r.skipped.length ? ` Not made: ${r.skipped.map((s) => `${s.name} (${s.reason})`).join("; ")}` : ""}`);
                      setPreview(null);
                      list.reload();
                    })
                  }
                >
                  Make crew invoices for {monthLabel(month)}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {list.loading && !list.data && <Skeleton columns={5} />}
      {list.error && <ErrorState message={list.error} onRetry={list.reload} />}
      {list.data && !rows.length && <EmptyState icon="dollar" title={`No crew invoices for ${monthLabel(month)}`}>They are made on the 1st of the next month from signed dockets.</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack">
            <thead><tr><th scope="col">Crew invoice</th><th scope="col">Crew member</th><th scope="col">Due</th><th scope="col" className="n">Total</th><th scope="col">Status</th></tr></thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id}>
                  <td data-label="Crew invoice"><a className="strong" href={`#/pay/${i.id}`}>{i.number}</a><span className="sub">{i.sent_at ? `Sent ${fmtDate(i.sent_at)}` : "Not sent"}</span></td>
                  <td data-label="Crew member">{i.snapshot.crew.name}{!i.is_valid && <span className="sub sub--primary">{i.invalid_reasons.join(" ")}</span>}</td>
                  <td data-label="Due" className="nowrap">{i.due_date ? fmtPlainDate(i.due_date) : "When valid"}</td>
                  <td data-label="Total" className="n">{aud(i.total)}{i.disputed_amount ? <span className="sub">{aud(payable(i))} payable</span> : null}</td>
                  <td data-label="Status">{chip(i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ one crew invoice */

function CrewInvoiceView({ id }: { id: string }) {
  const s = useAsync(() => crewInvoice(id), [id]);
  const [panel, setPanel] = useState<"" | "paid" | "dispute" | "void">("");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setNotice(null);
    try {
      ok(await fn());
      setPanel("");
      s.reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  if (s.loading && !s.data) return <SkeletonLines rows={6} />;
  if (s.error) return <ErrorState message={s.error} onRetry={s.reload} />;
  const raw = s.data!.invoice as Record<string, unknown>;
  const inv = {
    id: raw.id as string, number: raw.number as string, status: raw.status as Status, isValid: raw.isValid as boolean, invalidReasons: raw.invalidReasons as string[], dueDate: raw.dueDate as string | null,
    total: raw.total as number, subtotal: raw.subtotal as number, gst: raw.gst as number, sentAt: raw.sentAt as string | null, confirmedAt: raw.confirmedAt as string | null, queryNote: raw.queryNote as string | null,
    disputedAmount: raw.disputedAmount as number | null, disputeReason: raw.disputeReason as string | null, paidAt: raw.paidAt as string | null, paidAmount: raw.paidAmount as number | null,
    payMonth: raw.payMonth as string, snapshot: raw.snapshot as CrewInvoiceRow["snapshot"],
  };
  const lines = s.data!.lines;
  const open = inv.status !== "paid" && inv.status !== "void";
  const due = Math.round((inv.total - (inv.disputedAmount ?? 0)) * 100) / 100;
  const paidEarly = lines.filter((l) => l.clientPaid).length;

  return (
    <section className="page" aria-labelledby="page-title">
      <p className="small" style={{ margin: "0 0 8px" }}><a href="#/pay">Crew invoices</a></p>
      <PageHeader eyebrow={monthLabel(inv.payMonth)} title={`${inv.snapshot.title} ${inv.number}`} meta={<>{chip({ status: inv.status, is_valid: inv.isValid })} {inv.snapshot.crew.name}</>} actions={<button type="button" className="btn btn--outline btn--sm" onClick={() => void openBlobInNewTab(() => crewInvoicePdf(inv.id))}><Icon name="document" size={16} /> View PDF</button>} />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {!inv.isValid && open && (
        <p className="notice notice--warn" role="status">Not valid yet: {inv.invalidReasons.join(" ")} Add them on <a href="#/pay/details">Pay rates and details</a>, then <button type="button" className="linkbtn" disabled={busy} onClick={() => void run(async () => ((await revalidate(inv.id)).invoice.isValid ? "It is valid now and has a due date." : "Still not valid."))}>check again</button>.</p>
      )}
      {inv.queryNote && inv.status === "queried" && <p className="notice notice--warn" role="status">Queried by {inv.snapshot.crew.name}: {inv.queryNote}</p>}
      {inv.disputeReason && <p className="notice notice--warn" role="status">Disputed {aud(inv.disputedAmount ?? 0)}: {inv.disputeReason}</p>}
      <div className="card"><div className="card__body">
        <dl className="form-grid" style={{ margin: 0 }}>
          <div><dt className="field__label">Total</dt><dd>{aud(inv.total)}<span className="sub">{inv.gst ? `including ${aud(inv.gst)} GST` : "No GST: not registered"}</span></dd></div>
          <div><dt className="field__label">Payable</dt><dd>{aud(due)}</dd></div>
          <div><dt className="field__label">Due</dt><dd>{inv.dueDate ? fmtPlainDate(inv.dueDate) : "20 business days after it is valid"}</dd></div>
          <div><dt className="field__label">Sent</dt><dd>{inv.sentAt ? fmtDate(inv.sentAt) : <span className="muted">Not sent</span>}{inv.confirmedAt && <span className="sub">Confirmed {fmtDate(inv.confirmedAt)}</span>}</dd></div>
          <div><dt className="field__label">Client paid</dt><dd>{paidEarly ? `${paidEarly} of ${lines.length} shifts: can be paid early` : "Not yet"}</dd></div>
          {inv.paidAt && <div><dt className="field__label">Paid</dt><dd>{aud(inv.paidAmount ?? 0)} on {fmtPlainDate(inv.paidAt.slice(0, 10))}</dd></div>}
        </dl>
      </div></div>
      {open && (
        <div className="form-actions">
          <button type="button" className="btn btn--primary btn--sm" disabled={busy} aria-busy={busy} onClick={() => void run(async () => (await sendCrewInvoice(inv.id), `Sent to ${inv.snapshot.crew.email ?? "them"}.`))}><Icon name="mail" size={16} /> {inv.sentAt ? "Send again" : "Send to crew member"}</button>
          {inv.isValid && <button type="button" className="btn btn--ink btn--sm" onClick={() => setPanel("paid")}>Mark paid</button>}
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setPanel("dispute")}>Disputed amount notice</button>
          <button type="button" className="btn btn--outline btn--sm" onClick={() => setPanel("void")}>Void</button>
        </div>
      )}
      {panel === "paid" && <PaidForm max={due} busy={busy} onCancel={() => setPanel("")} onSave={(on, amount, ref) => run(async () => (await markPaid(inv.id, on, amount, ref), "Marked paid."))} />}
      {panel === "dispute" && <DisputeForm max={inv.total} busy={busy} onCancel={() => setPanel("")} onSave={(amount, reason) => run(async () => ((await disputeAmount(inv.id, amount, reason)).emailed ? "The notice was emailed and is kept on the crew invoice." : "Saved. They have no email, so tell them yourself."))} />}
      {panel === "void" && (
        <form className="card card__body form-grid" onSubmit={(e) => { e.preventDefault(); const r = String(new FormData(e.currentTarget).get("reason") ?? ""); void run(async () => (await voidCrewInvoice(inv.id, r), "Voided. Its hours can go on a new crew invoice.")); }}>
          <Field label="Why it is void" hint="Its hours become free for a new crew invoice for the month." wide><textarea className="input" name="reason" rows={2} required minLength={5} /></Field>
          <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--sm" disabled={busy}>Void</button><button type="button" className="btn btn--outline btn--sm" onClick={() => setPanel("")}>Cancel</button></div>
        </form>
      )}
      <div className="card">
        <div className="card__head"><h2 className="card__title">Shifts</h2></div>
        <div className="table-wrap table-wrap--flat">
          <table className="table table--stack">
            <thead><tr><th scope="col">Shift</th><th scope="col" className="n">Hours</th><th scope="col" className="n">Overtime</th><th scope="col" className="n">Amount</th><th scope="col">Client paid</th></tr></thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.docketCrewId}>
                  <td data-label="Shift">{l.description}</td>
                  <td data-label="Hours" className="n">{hrs(l.dayMinutes + l.nightMinutes + l.weekendMinutes + l.publicHolidayMinutes)}</td>
                  <td data-label="Overtime" className="n">{l.overtimeMinutes ? hrs(l.overtimeMinutes) : ""}</td>
                  <td data-label="Amount" className="n">{aud(l.amount)}</td>
                  <td data-label="Client paid">{l.clientPaid ? <Chip tone="green">Paid</Chip> : <span className="muted">Not yet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function PaidForm({ max, busy, onCancel, onSave }: { max: number; busy: boolean; onCancel: () => void; onSave: (on: string, amount: number, ref: string | null) => void }) {
  const [on, setOn] = useState(localYmd());
  const [amount, setAmount] = useState(String(max));
  const [ref, setRef] = useState("");
  return (
    <form className="card card__body form-grid" onSubmit={(e: FormEvent) => { e.preventDefault(); onSave(on, Number(amount), ref.trim() || null); }}>
      <Field label="Paid on"><input className="input" type="date" required value={on} max={localYmd()} onChange={(e) => setOn(e.target.value)} /></Field>
      <Field label="Amount"><input className="input" type="number" min={0.01} step={0.01} required value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="Bank reference"><input className="input" maxLength={120} value={ref} onChange={(e) => setRef(e.target.value)} /></Field>
      <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--sm" disabled={busy}>Mark paid</button><button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Cancel</button></div>
    </form>
  );
}

function DisputeForm({ max, busy, onCancel, onSave }: { max: number; busy: boolean; onCancel: () => void; onSave: (amount: number, reason: string) => void }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  return (
    <form className="card card__body form-grid" onSubmit={(e: FormEvent) => { e.preventDefault(); onSave(Number(amount), reason); }}>
      <p className="small muted field--wide">A written notice to the crew member of the amount you dispute and why. The rest is paid as usual.</p>
      <Field label="Amount disputed"><input className="input" type="number" min={0.01} max={max} step={0.01} required value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="Why" wide><textarea className="input" rows={2} required minLength={5} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--sm" disabled={busy}>Send the notice</button><button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Cancel</button></div>
    </form>
  );
}

/* ------------------------------------------------------------------ rates and details */

const BANDS: { key: keyof Rates; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "night", label: "Night" },
  { key: "weekend", label: "Weekend" },
  { key: "publicHoliday", label: "Public holiday" },
  { key: "overtime", label: "Overtime" },
];

function RatesForm({ value, busy, onSave, onCancel, extra }: { value: Rates | null; busy: boolean; onSave: (r: Rates) => void; onCancel?: () => void; extra?: ReactNode }) {
  const [r, setR] = useState<Record<keyof Rates, string>>(() => Object.fromEntries(BANDS.map((b) => [b.key, value ? String(value[b.key]) : ""])) as Record<keyof Rates, string>);
  return (
    <form className="form-grid" onSubmit={(e) => { e.preventDefault(); onSave(Object.fromEntries(BANDS.map((b) => [b.key, Number(r[b.key])])) as unknown as Rates); }}>
      {BANDS.map((b) => (
        <Field key={b.key} label={`${b.label} per hour`}><input className="input" type="number" min={0} step={0.01} required value={r[b.key]} onChange={(e) => setR({ ...r, [b.key]: e.target.value })} /></Field>
      ))}
      <div className="form-actions field--wide">
        <button type="submit" className="btn btn--ink btn--sm" disabled={busy}>Save rates</button>
        {onCancel && <button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Cancel</button>}
        {extra}
      </div>
    </form>
  );
}

function PayDetails() {
  const s = useAsync(async () => {
    const [members, rates, role] = await Promise.all([payMembers(), payRates(), myRole()]);
    return { members, rates, role, banks: role === "director" ? await banks() : {} };
  });
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const active = useMemo(() => (s.data?.members ?? []).filter((m) => m.active), [s.data]);
  async function run(fn: () => Promise<void>, done: string) {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      ok(done);
      setEditing(null);
      s.reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  if (s.loading && !s.data) return <section className="page"><Skeleton columns={4} /></section>;
  if (s.error) return <section className="page"><ErrorState message={s.error} onRetry={s.reload} /></section>;
  const { rates, role } = s.data!;
  const director = role === "director";
  const today = localYmd();
  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader eyebrow="Accounts" title={PAY_NAV.label} meta="Pay rates by band, and what each crew member needs on file to be paid: ABN, GST, insurance and bank details." />
      <Tabs current="details" />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="card">
        <div className="card__head"><h2 className="card__title">Standard pay rates</h2></div>
        <div className="card__body stack">
          <p className="small muted">Paid per hour by band up to each docket's overtime threshold (8 hours), then at the overtime rate. Crew members with their own rates are set below.{director ? "" : " Only a director can change rates."}</p>
          {!rates.standard && <p className="notice notice--warn small" role="status">No standard pay rates yet. Crew invoices are not made until they are set.</p>}
          {director ? (
            <RatesForm key={JSON.stringify(rates.standard)} value={rates.standard} busy={busy} onSave={(r) => void run(() => saveRates(null, r), "Standard pay rates saved.")} />
          ) : rates.standard ? (
            <p className="small">{BANDS.map((b) => `${b.label} ${aud(rates.standard![b.key])}`).join(", ")}</p>
          ) : null}
        </div>
      </div>
      <div className="card">
        <div className="card__head"><h2 className="card__title">Crew pay details</h2></div>
        <ul className="list">
          {active.map((m) => {
            const own = rates.byMember[m.id];
            const bank = s.data!.banks[m.id];
            const issues = m.is_partner ? [] : [!m.abn && "No ABN", !m.public_liability_expiry ? "No insurance" : m.public_liability_expiry < today ? "Insurance expired" : null, director && !bank && "No bank details"].filter(Boolean) as string[];
            return (
              <li key={m.id} className="list__row" style={{ flexWrap: "wrap" }}>
                <span>
                  <strong>{m.full_name}</strong>
                  <span className="sub">{m.is_partner ? "Partner: not paid by crew invoice" : [m.abn ? `ABN ${m.abn}` : null, m.gst_registered ? "GST registered" : "Not GST registered", own ? "Own rates" : "Standard rates"].filter(Boolean).join(", ")}</span>
                  {issues.length > 0 && <span className="sub sub--primary">{issues.join(", ")}</span>}
                </span>
                {editing !== m.id && <button type="button" className="btn btn--outline btn--xs" onClick={() => setEditing(m.id)}>Edit</button>}
                {editing === m.id && <MemberForm m={m} own={own ?? null} bank={bank ?? null} director={director} busy={busy} run={run} onCancel={() => setEditing(null)} />}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function MemberForm({ m, own, bank, director, busy, run, onCancel }: {
  m: PayMember; own: Rates | null; bank: { account_name: string; bsb: string; account_number: string } | null; director: boolean; busy: boolean;
  run: (fn: () => Promise<void>, done: string) => Promise<void>; onCancel: () => void;
}) {
  const [f, setF] = useState({ is_partner: m.is_partner, abn: m.abn ?? "", gst_registered: m.gst_registered, super_entity: m.super_entity ?? "", public_liability_expiry: m.public_liability_expiry ?? "" });
  const [b, setB] = useState({ account_name: bank?.account_name ?? "", bsb: bank?.bsb ?? "", account_number: bank?.account_number ?? "" });
  const [ownRates, setOwnRates] = useState(Boolean(own));
  return (
    <div className="stack" style={{ width: "100%", marginTop: 10 }}>
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          const abn = f.abn.replace(/\s/g, "");
          void run(
            () => savePayDetails(m.id, { is_partner: f.is_partner, abn: abn || null, gst_registered: f.gst_registered, super_entity: (f.super_entity || null) as PayMember["super_entity"], ...(BRAND.crewInsurance ? {} : { public_liability_expiry: f.public_liability_expiry || null }) }),
            `${m.full_name}'s pay details saved.`,
          );
        }}
      >
        <label className="check field--wide"><input type="checkbox" checked={f.is_partner} onChange={(e) => setF({ ...f, is_partner: e.target.checked })} /> Partner (not paid by crew invoice)</label>
        <Field label="ABN"><input className="input" inputMode="numeric" pattern="[\d ]{11,14}" value={f.abn} onChange={(e) => setF({ ...f, abn: e.target.value })} /></Field>
        {BRAND.crewInsurance ? (
          <div className="field"><span className="field__label">Public liability insurance</span><span className="small">{m.public_liability_expiry ? `Certificate on file, expires ${fmtPlainDate(m.public_liability_expiry)}` : "No certificate on file"}. Certificates are added on <a href="#/crew">Crew & tickets</a>, Insurance.</span></div>
        ) : (
          <Field label="Public liability insurance expires" hint="Valid crew invoices need current insurance"><input className="input" type="date" value={f.public_liability_expiry} onChange={(e) => setF({ ...f, public_liability_expiry: e.target.value })} /></Field>
        )}
        <Field label="Business type" hint="For the super report">
          <select className="input" value={f.super_entity} onChange={(e) => setF({ ...f, super_entity: e.target.value })}>
            <option value="">Not recorded</option><option value="individual">Individual (sole trader)</option><option value="company">Company</option><option value="trust">Trust</option>
          </select>
        </Field>
        <label className="check"><input type="checkbox" checked={f.gst_registered} onChange={(e) => setF({ ...f, gst_registered: e.target.checked })} /> Registered for GST</label>
        <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--sm" disabled={busy}>Save details</button><button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Close</button></div>
      </form>
      {director && (
        <form className="form-grid" onSubmit={(e) => { e.preventDefault(); void run(() => saveBank(m.id, { account_name: b.account_name.trim(), bsb: b.bsb.trim(), account_number: b.account_number.replace(/\s/g, "") }), "Bank details saved."); }}>
          <p className="small strong field--wide" style={{ margin: 0 }}>Bank details (directors only)</p>
          <Field label="Account name"><input className="input" required value={b.account_name} onChange={(e) => setB({ ...b, account_name: e.target.value })} autoComplete="off" /></Field>
          <Field label="BSB"><input className="input" required inputMode="numeric" pattern="\d{3}-?\d{3}" value={b.bsb} onChange={(e) => setB({ ...b, bsb: e.target.value })} autoComplete="off" /></Field>
          <Field label="Account number"><input className="input" required inputMode="numeric" pattern="[\d ]{5,12}" value={b.account_number} onChange={(e) => setB({ ...b, account_number: e.target.value })} autoComplete="off" /></Field>
          <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--sm" disabled={busy}>Save bank details</button></div>
        </form>
      )}
      {director && (
        <div className="stack">
          <label className="check"><input type="checkbox" checked={ownRates} onChange={(e) => { setOwnRates(e.target.checked); if (!e.target.checked && own) void run(() => removeOwnRates(m.id), "Back on the standard rates."); }} /> Own pay rates</label>
          {ownRates && <RatesForm value={own} busy={busy} onSave={(r) => void run(() => saveRates(m.id, r), `${m.full_name}'s own rates saved.`)} />}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ the crew member's statements */

export function MyPay() {
  const s = useAsync(myStatements);
  const [query, setQuery] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const list = s.data?.statements ?? [];
  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      ok(done);
      setQuery(null);
      setNote("");
      s.reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-labelledby="page-title">
      <a className="backlink" href="#/"><Icon name="arrow" size={16} /> Your shifts</a>
      <header className="packhead"><h1 id="page-title" tabIndex={-1}>Pay</h1><p className="packhead__sub">Your monthly pay statements from signed dockets.</p></header>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {s.loading && !s.data && <SkeletonLines rows={3} />}
      {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
      {s.data && !list.length && <EmptyState icon="dollar" title="No pay statements yet">Each month's statement shows here once the office sends it.</EmptyState>}
      {list.map((i) => (
        <div key={i.id} className="card">
          <div className="card__head"><h2 className="card__title">{monthLabel(i.month)}</h2>{chip({ status: i.status, is_valid: i.isValid })}</div>
          <div className="card__body stack">
            <p className="strong" style={{ margin: 0 }}>{aud(i.total)}{i.gst ? ` including ${aud(i.gst)} GST` : ""}</p>
            <p className="small muted" style={{ margin: 0 }}>{i.number}. {i.paidAt ? `Paid ${fmtPlainDate(i.paidAt.slice(0, 10))}.` : i.dueDate ? `Due ${fmtPlainDate(i.dueDate)}.` : `Not valid yet: ${i.invalidReasons.join(" ")}`}</p>
            {i.disputeReason && <p className="notice notice--warn small" role="status">Disputed {aud(i.disputedAmount ?? 0)}: {i.disputeReason}</p>}
            <div className="form-actions">
              <button type="button" className="btn btn--outline btn--sm" onClick={() => void openBlobInNewTab(() => myStatementPdf(i.id))}>View PDF</button>
              {(i.status === "issued" || i.status === "queried") && <button type="button" className="btn btn--ink btn--sm" disabled={busy} onClick={() => void run(() => confirmStatement(i.id), "Confirmed. Thanks.")}>Confirm</button>}
              {i.status !== "paid" && query !== i.id && <button type="button" className="btn btn--outline btn--sm" onClick={() => setQuery(i.id)}>Query</button>}
            </div>
            {query === i.id && (
              <form className="form" onSubmit={(e) => { e.preventDefault(); void run(() => queryStatement(i.id, note), "Sent to the office."); }}>
                <Field label="What looks wrong?"><textarea className="input" rows={3} required minLength={5} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
                <div className="form-actions"><button type="submit" className="btn btn--ink btn--sm" disabled={busy}>Send to the office</button><button type="button" className="btn btn--outline btn--sm" onClick={() => setQuery(null)}>Cancel</button></div>
              </form>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}
